/**
 * WaveformPreview — minimal, drop-in static waveform renderer.
 *
 * Single responsibility: given a Canvas + AudioBuffer, paint a peaks-style
 * waveform AND return a silence map (so callers can show "X seconds of
 * silence detected" without re-scanning the same samples).
 *
 * Why it exists:
 *   /repair-audio user 4f1ac99d (2026-05-12) uploaded a 3:31 MP3 where
 *   1:04–3:29 is real silence. He hit play, heard voice cut at 1:04,
 *   thought our repair broke the file. A waveform would have shown the
 *   flat region instantly — no "voice not coming" feedback needed.
 *
 * Out of scope:
 *   No interactivity (trim handles, region selection, hover scrub) —
 *   that's /trim's drawWaveform. This module is a STATIC preview only.
 *   No spectrogram, no fade overlays, no zoom/pan, no live frequency bars.
 *
 * API:
 *   var info = window.WaveformPreview.render(canvas, audioBuffer, {
 *       color: '#38bdf8',       // peak color (default: --accent-ish sky-400)
 *       bgColor: 'transparent',
 *       silenceThresholdDb: -42,// columns below this peak are "silent"
 *       silenceMinSec: 1.0,     // shorter runs aren't counted as a region
 *       compareBuffer: prevBuf  // if set, draw both stacked top/bottom
 *   });
 *   // → { silenceRatio, signalDuration, silenceDuration, regions, leading, trailing }
 *
 *   You don't have to use the silence info — drawing is the primary purpose.
 *   But it's already computed during the column scan, so we hand it back free.
 *
 * Since 2026-09-04 — the size-blind path:
 *   var peaks = await WaveformPreview.peaksFromPcm(file, layout, { columns: 1024 });
 *   var info  = WaveformPreview.renderPeaks(canvas, peaks, { compare: otherPeaks });
 *
 *   `render()` needs the WHOLE recording decoded into float32 — for a 967 MB
 *   WAV that is ~1.9 GB of AudioBuffer, and on the /repair-audio page it was
 *   produced by a full FFmpeg transcode when the native decoder balked: 11 GB
 *   of browser RSS in Firefox for a picture (measured 2026-09-04). PCM needs no
 *   decoder: `peaksFromPcm` streams the payload in 4 MB slices and keeps only
 *   one min/max pair per column, so the cost is a sequential read of the file
 *   and a few KB of memory, whatever the size. `layout` is the WavSurgeon
 *   `fmt` (formatTag / channels / sampleRate / bitsPerSample) plus `offset` and
 *   `bytes` of the payload, or a PcmLayoutGuess candidate plus `sampleRate`.
 */
(function(){
'use strict';

if (window.WaveformPreview && !window.WaveformPreview.__isStub) return;

var DEFAULT_COLOR = 'rgba(56,189,248,0.80)';       // sky-400 — matches lg-card accents
var DEFAULT_BG = 'transparent';

// Silence cutoff in linear amplitude. -42 dB ≈ 0.00794; default keeps room-tone
// out of "silence" while flagging WhatsApp-style true silence at -inf / -60+ dB.
function dbToAmp(db){ return Math.pow(10, db / 20); }

function clampDpr(n, dpr){ return Math.max(1, Math.floor(n * dpr)); }

/**
 * Walk the AudioBuffer once, producing per-column (min, max) peaks AND a
 * silence-column bitmap. Single pass, no allocation per sample.
 *
 * For multi-channel input we take max(|sample|) across channels per sample —
 * mono mix would understate signal in stereo recordings where one channel is
 * silent (a real edge case for podcast / interview setups).
 */
function buildPeaks(buffer, columns, silenceAmp){
    var chans = buffer.numberOfChannels;
    var len = buffer.length;
    var samplesPerCol = len / columns;
    var mins = new Float32Array(columns);
    var maxs = new Float32Array(columns);
    var silent = new Uint8Array(columns);

    // Cache channel Float32Arrays — getChannelData is cheap-but-not-free.
    var data = new Array(chans);
    for (var c = 0; c < chans; c++) data[c] = buffer.getChannelData(c);

    for (var col = 0; col < columns; col++) {
        var s0 = Math.floor(col * samplesPerCol);
        var s1 = Math.floor((col + 1) * samplesPerCol);
        if (s1 <= s0) s1 = s0 + 1;
        var mn = 1, mx = -1;
        var peak = 0;
        for (var i = s0; i < s1; i++) {
            var v = 0;
            for (var c = 0; c < chans; c++) {
                var vv = data[c][i];
                if (Math.abs(vv) > Math.abs(v)) v = vv;
            }
            if (v < mn) mn = v;
            if (v > mx) mx = v;
            var av = Math.abs(v);
            if (av > peak) peak = av;
        }
        mins[col] = mn;
        maxs[col] = mx;
        if (peak < silenceAmp) silent[col] = 1;
    }
    return { mins: mins, maxs: maxs, silent: silent, duration: buffer.duration };
}

/**
 * Coalesce silent columns into regions { start, end } in SECONDS,
 * dropping anything shorter than minSec. Also computes leading/trailing
 * silence (silent run starting at col 0 / ending at col W-1).
 */
function silenceRegions(silent, totalDur, minSec){
    var w = silent.length;
    var secPerCol = totalDur / w;
    var regions = [];
    var runStart = -1;
    for (var i = 0; i < w; i++) {
        if (silent[i]) {
            if (runStart < 0) runStart = i;
        } else if (runStart >= 0) {
            var dur = (i - runStart) * secPerCol;
            if (dur >= minSec) {
                regions.push({ start: runStart * secPerCol, end: i * secPerCol, dur: dur });
            }
            runStart = -1;
        }
    }
    if (runStart >= 0) {
        var dur = (w - runStart) * secPerCol;
        if (dur >= minSec) {
            regions.push({ start: runStart * secPerCol, end: w * secPerCol, dur: dur });
        }
    }
    var silenceDuration = 0;
    for (var k = 0; k < regions.length; k++) silenceDuration += regions[k].dur;
    var leading = (regions.length && regions[0].start <= secPerCol) ? regions[0].dur : 0;
    var trailing = (regions.length && regions[regions.length-1].end >= totalDur - secPerCol)
        ? regions[regions.length-1].dur : 0;
    return { regions: regions, silenceDuration: silenceDuration, leading: leading, trailing: trailing };
}

function paintPeaks(ctx, peaks, x0, y0, w, h, color){
    var mid = y0 + h / 2;
    var mins = peaks.mins, maxs = peaks.maxs;
    var cols = mins.length;
    var scaleX = w / cols;
    ctx.fillStyle = color;
    for (var i = 0; i < cols; i++) {
        var top = mid - maxs[i] * (h / 2);
        var bot = mid - mins[i] * (h / 2);
        if (bot - top < 1) { top = mid - 0.5; bot = mid + 0.5; }
        ctx.fillRect(x0 + i * scaleX, top, Math.max(1, scaleX), bot - top);
    }
}

function summarize(peaks, silenceMinSec){
    var sil = silenceRegions(peaks.silent, peaks.duration, silenceMinSec);
    return {
        totalDuration: peaks.duration,
        signalDuration: peaks.duration - sil.silenceDuration,
        silenceDuration: sil.silenceDuration,
        silenceRatio: peaks.duration > 0 ? sil.silenceDuration / peaks.duration : 0,
        regions: sil.regions,
        leading: sil.leading,
        trailing: sil.trailing
    };
}

/* Resample a peaks object to `columns` columns (max/min over merged bins). */
function fitColumns(peaks, columns){
    if (peaks.mins.length === columns) return peaks;
    var mins = new Float32Array(columns), maxs = new Float32Array(columns), silent = new Uint8Array(columns);
    var n = peaks.mins.length;
    for (var col = 0; col < columns; col++) {
        var a = Math.floor(col * n / columns), b = Math.max(a + 1, Math.floor((col + 1) * n / columns));
        var mn = 1, mx = -1, sil = 1;
        for (var i = a; i < b && i < n; i++) {
            if (peaks.mins[i] < mn) mn = peaks.mins[i];
            if (peaks.maxs[i] > mx) mx = peaks.maxs[i];
            if (!peaks.silent[i]) sil = 0;
        }
        mins[col] = mn; maxs[col] = mx; silent[col] = sil;
    }
    return { mins: mins, maxs: maxs, silent: silent, duration: peaks.duration };
}

function prepareCanvas(canvas, bg, opts){
    var dpr = window.devicePixelRatio || 1;
    var cssW = canvas.clientWidth || canvas.width || 600;
    var cssH = canvas.clientHeight || canvas.height || 80;
    canvas.width = clampDpr(cssW, dpr);
    canvas.height = clampDpr(cssH, dpr);
    var ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (bg !== 'transparent') { ctx.fillStyle = bg; ctx.fillRect(0, 0, cssW, cssH); }
    else ctx.clearRect(0, 0, cssW, cssH);
    return { ctx: ctx, w: cssW, h: cssH };
}

/**
 * Paint precomputed peaks (from `peaksFromPcm` or `buildPeaks`). Same return
 * shape as render(). `opts.compare` is a second peaks object drawn below.
 */
function renderPeaks(canvas, peaks, opts){
    opts = opts || {};
    var c = prepareCanvas(canvas, opts.bgColor || DEFAULT_BG, opts);
    var silenceMinSec = opts.silenceMinSec !== undefined ? opts.silenceMinSec : 1.0;
    var rowH = opts.compare ? Math.floor(c.h / 2) : c.h;
    var p = fitColumns(peaks, c.w);
    paintPeaks(c.ctx, p, 0, 0, c.w, rowH, opts.color || DEFAULT_COLOR);
    if (opts.compare) {
        paintPeaks(c.ctx, fitColumns(opts.compare, c.w), 0, rowH, c.w, c.h - rowH,
                   opts.compareColor || 'rgba(34,197,94,0.80)');
    }
    return summarize(peaks, silenceMinSec);
}

function render(canvas, buffer, opts){
    opts = opts || {};
    var silenceThresholdDb = opts.silenceThresholdDb !== undefined ? opts.silenceThresholdDb : -42;
    var silenceAmp = dbToAmp(silenceThresholdDb);
    var c = prepareCanvas(canvas, opts.bgColor || DEFAULT_BG, opts);
    var compareBuffer = opts.compareBuffer || null;
    var rowH = compareBuffer ? Math.floor(c.h / 2) : c.h;
    var peaks = buildPeaks(buffer, c.w, silenceAmp);
    paintPeaks(c.ctx, peaks, 0, 0, c.w, rowH, opts.color || DEFAULT_COLOR);
    if (compareBuffer) {
        // Bottom row: comparison (e.g., "after" repair). Use a contrasting hue.
        var peaks2 = buildPeaks(compareBuffer, c.w, silenceAmp);
        paintPeaks(c.ctx, peaks2, 0, rowH, c.w, c.h - rowH,
                   opts.compareColor || 'rgba(34,197,94,0.80)');
    }
    return summarize(peaks, opts.silenceMinSec !== undefined ? opts.silenceMinSec : 1.0);
}

/* ── PCM readers for the streaming path ─────────────────────────────────── */
function pcmReader(layout){
    // WavSurgeon fmt: formatTag 1 = int LE, 3 = float LE (EXTENSIBLE resolved by caller).
    // PcmLayoutGuess: fmt id string.
    var id = layout.fmt && typeof layout.fmt === 'string' ? layout.fmt
        : (layout.formatTag === 3 ? 'f32le'
            : layout.bitsPerSample === 8 ? 'u8'
            : 's' + layout.bitsPerSample + 'le');
    var bps = id === 'u8' ? 1 : parseInt(id.replace(/^[sf]/, ''), 10) / 8;
    var be = /be$/.test(id);
    var read;
    switch (bps) {
        case 1: read = function(v, i){ return (v.getUint8(i) - 128) / 128; }; break;
        case 2: read = function(v, i){ return v.getInt16(i, !be) / 32768; }; break;
        case 3: read = be
            ? function(v, i){ return ((v.getUint8(i + 2) | (v.getUint8(i + 1) << 8) | (v.getUint8(i) << 16)) << 8 >> 8) / 8388608; }
            : function(v, i){ return ((v.getUint8(i) | (v.getUint8(i + 1) << 8) | (v.getUint8(i + 2) << 16)) << 8 >> 8) / 8388608; };
            break;
        default: read = id.charAt(0) === 'f'
            ? function(v, i){ var x = v.getFloat32(i, !be); return x === x ? Math.max(-1, Math.min(1, x)) : 0; }
            : function(v, i){ return v.getInt32(i, !be) / 2147483648; };
    }
    return { bps: bps, read: read };
}

/**
 * Peaks straight from PCM bytes — streamed, bounded, decoder-free.
 *
 * @param {File|Blob} file
 * @param {object} layout  { channels, sampleRate, offset, bytes?, fmt | formatTag+bitsPerSample, shift? }
 * @param {{columns?:number, silenceThresholdDb?:number, sliceBytes?:number, signal?:AbortSignal, onProgress?:function}} [opts]
 * @returns {Promise<{mins:Float32Array, maxs:Float32Array, silent:Uint8Array, duration:number, chPeak:Float32Array}>}
 */
async function peaksFromPcm(file, layout, opts){
    opts = opts || {};
    var columns = opts.columns || 1024;
    var silenceAmp = dbToAmp(opts.silenceThresholdDb !== undefined ? opts.silenceThresholdDb : -42);
    var sliceBytes = opts.sliceBytes || 4 * 1048576;
    var r = pcmReader(layout);
    var ch = layout.channels | 0 || 1;
    var block = r.bps * ch;
    var start = (layout.offset || 0) + (layout.shift || 0);
    var total = Math.min(layout.bytes > 0 ? layout.bytes : Infinity, file.size - start);
    var frames = Math.floor(total / block);
    var rate = layout.sampleRate || 44100;
    var mins = new Float32Array(columns), maxs = new Float32Array(columns), silent = new Uint8Array(columns);
    var peakCol = new Float32Array(columns);
    // Absolute peak of EVERY channel over the whole scan. The columns fold the
    // channels together (the picture wants the louder one), and that fold hides
    // the one fact a listener complains about: "sound in one ear only" is a
    // stereo file whose second channel never moved. Two floats; SoundVerdict
    // reads them.
    var chPeak = new Float32Array(ch);
    for (var k = 0; k < columns; k++) { mins[k] = 1; maxs[k] = -1; }
    var framesPerCol = frames / columns;
    // Sample every Nth frame when the file is huge: 1 GB at 44.1 kHz stereo is
    // 242 M frames — reading each one is CPU, not I/O. 64 frames per column
    // per slice is plenty for min/max.
    var step = Math.max(1, Math.floor(framesPerCol / 4096));
    var sliceFrames = Math.floor(sliceBytes / block);
    for (var f0 = 0; f0 < frames; f0 += sliceFrames) {
        if (opts.signal && opts.signal.aborted) throw new Error('Aborted');
        var n = Math.min(sliceFrames, frames - f0);
        var at = start + f0 * block;
        var v = new DataView(await file.slice(at, at + n * block).arrayBuffer());
        for (var f = 0; f < n; f += step) {
            var col = Math.min(columns - 1, Math.floor((f0 + f) / framesPerCol));
            var base = f * block, val = 0;
            for (var c = 0; c < ch; c++) {
                var x = r.read(v, base + c * r.bps);
                var ax = x < 0 ? -x : x;
                if (ax > chPeak[c]) chPeak[c] = ax;
                if (ax > Math.abs(val)) val = x;
            }
            if (val < mins[col]) mins[col] = val;
            if (val > maxs[col]) maxs[col] = val;
            var a = Math.abs(val);
            if (a > peakCol[col]) peakCol[col] = a;
        }
        if (opts.onProgress) try { opts.onProgress((f0 + n) / frames); } catch(_){}
    }
    for (var q = 0; q < columns; q++) {
        if (mins[q] > maxs[q]) { mins[q] = 0; maxs[q] = 0; }
        if (peakCol[q] < silenceAmp) silent[q] = 1;
    }
    return { mins: mins, maxs: maxs, silent: silent, duration: frames / rate, chPeak: chPeak };
}

window.WaveformPreview = {
    __isStub: false,
    version: '1.3.0',
    render: render,
    renderPeaks: renderPeaks,
    peaksFromPcm: peaksFromPcm,
    // Peaks as DATA plus a painter that takes an arbitrary rectangle.
    //
    // `render()` assumes one buffer fills one canvas — true for a preview
    // card, false for a shared time axis where several parts sit side by side
    // at their own offsets and each occupies a slice of the same canvas. The
    // pieces to do that already existed inside this module; the alternative
    // was a second peak-builder in the tool, and a second copy of a
    // measurement is how two pictures of the same sound start disagreeing.
    //
    // `peaks(buffer, columns)` → { mins, maxs, silent, duration }
    // `paint(ctx, peaks, x, y, w, h, color)` — resamples to `w` columns.
    peaks: function (buffer, columns, opts) {
        opts = opts || {};
        var amp = dbToAmp(opts.silenceThresholdDb !== undefined ? opts.silenceThresholdDb : -42);
        return buildPeaks(buffer, Math.max(1, Math.round(columns || 512)), amp);
    },
    paint: function (ctx, peaks, x, y, w, h, color) {
        if (!ctx || !peaks || !(w > 0) || !(h > 0)) return;
        var cols = Math.max(1, Math.round(w));
        paintPeaks(ctx, fitColumns(peaks, cols), x, y, cols, h, color || DEFAULT_COLOR);
    },
    // Exposed for tests / specialised callers that want just the silence map
    // without painting.
    analyze: function(buffer, opts){
        opts = opts || {};
        var silenceAmp = dbToAmp(opts.silenceThresholdDb !== undefined ? opts.silenceThresholdDb : -42);
        var silenceMinSec = opts.silenceMinSec !== undefined ? opts.silenceMinSec : 1.0;
        var columns = opts.columns || 1024;
        return summarize(buildPeaks(buffer, columns, silenceAmp), silenceMinSec);
    }
};

})();
